import { Injectable, inject } from '@angular/core';
import { IqEventBusBrokerService } from '@dat/iq-event-bus';
import {
    AddControlPayload,
    AddControlPayloadSchema,
    DisableControlPayload,
    DisableControlPayloadSchema,
    EnableControlPayload,
    EnableControlPayloadSchema,
    PatchDashboardControlsValuePayload,
    PatchDashboardControlsValuePayloadSchema,
    RemoveControlPayload,
    SetControlPropPayload,
    SetControlPropPayloadSchema,
    SetControlValuePayload,
    SetControlValuePayloadSchema,
    SetDashboardControlsValuePayload,
    SetDashboardControlsValuePayloadSchema,
    UpdateFormCachePayload,
    UpdateFormCachePayloadSchema,
    UpdateLabelPayload,
    UpdateLabelPayloadSchema,
    UpdateTabsPayload,
    UpdateTabsPayloadSchema,
    removeControlPayloadSchema,
} from './iq-controls-action.models';
import { IqControlsStatusService } from './iq-controls-status.service';
import { Tab } from '@dat/iq-control-tab';

@Injectable({
    providedIn: 'root',
})
export class IqControlsActionService {
    private readonly INVALID_PAYLOAD_MESSAGE = 'Invalid payload';
    private _events: IqEventBusBrokerService = inject(IqEventBusBrokerService);

    protected _statusService = inject(IqControlsStatusService);

    setControlProp(payload: SetControlPropPayload): void {
        const test: any = SetControlPropPayloadSchema.safeParse(payload);
        if (!test.success) {
            console.warn('SetControlPropPayloadSchema Invalid Payload');
            console.log(test.error.issues);
            return;
        }

        this._statusService.setControlProps(
            payload.dashboardControlId,
            payload.controlName,
            payload.props,
        );
    }
    patchDashboardControlsValue(
        payload: PatchDashboardControlsValuePayload,
    ): void {
        const test: any =
            PatchDashboardControlsValuePayloadSchema.safeParse(payload);
        if (!test.success) {
            console.warn(
                'PatchDashboardControlsValuePayloadSchema Invalid Payload',
            );
            console.log(test.error.issues);
            return;
        }

        this._statusService.patchDashboardControlsValue(
            payload.dashboardControlId,
            payload.value,
        );
    }
    setDashboardControlsValue(payload: SetDashboardControlsValuePayload): void {
        const test: any =
            SetDashboardControlsValuePayloadSchema.safeParse(payload);
        if (!test.success) {
            console.warn(
                'SetDashboardControlsValuePayloadSchema Invalid Payload',
            );
            console.log(test.error.issues);
            return;
        }

        this._statusService.setDashboardControlsValue(
            payload.dashboardControlId,
            payload.value,
        );
    }

    setControlValue(payload: SetControlValuePayload): void {
        const test: any = SetControlValuePayloadSchema.safeParse(payload);

        if (!test.success) {
            console.warn(
                'SetDashboardControlsValuePayloadSchema Invalid Payload',
            );
            console.log(test.error.issues);
            return;
        }

        this._statusService.setControlValue(
            payload.dashboardControlId,
            payload.controlName,
            payload.value,
        );
    }

    enableControl(payload: EnableControlPayload): void {
        const test: any = EnableControlPayloadSchema.safeParse(payload);
        if (!test.success) {
            console.warn('enableControl Invalid Payload');
            console.log(test.error.issues);
            return;
        }

        this._statusService.setControlEnablement(
            payload.dashboardControlId,
            payload.controlName,
            true,
        );
    }

    disableControl(payload: DisableControlPayload): void {
        const test: any = DisableControlPayloadSchema.safeParse(payload);
        if (!test.success) {
            console.warn('disableControl Invalid Payload');
            console.log(test.error.issues);
            return;
        }

        this._statusService.setControlEnablement(
            payload.dashboardControlId,
            payload.controlName,
            false,
        );
    }

    addControl(payload: AddControlPayload): void {
        const test: any = AddControlPayloadSchema.safeParse(payload);
        if (!test.success) {
            console.warn('AddControlPayloadSchema Invalid Payload');
            console.log(test.error.issues);
            return;
        }

        this._statusService.addControl(
            payload.dashboardControlId,
            payload.control,
            payload.indexAt,
        );
    }

    removeControl(payload: RemoveControlPayload): void {
        const test: any = removeControlPayloadSchema.safeParse(payload);
        if (!test.success) {
            console.warn('removeControlPayloadSchema Invalid Payload');
            console.log(test.error.issues);
            return;
        }

        this._statusService.removeControl(
            payload.dashboardControlId,
            payload.controlNames,
        );
    }

    updateFormCache(payload: UpdateFormCachePayload): void {
        const test: any = UpdateFormCachePayloadSchema.safeParse(payload);
        if (!test.success) {
            console.warn('UpdateFormCachePayloadSchema Invalid Payload');
            console.log(test.error.issues);
            return;
        }

        this._statusService.updateFormCache(payload.formId, payload.data);
    }

    typeTabsUpdateTabs(payload: {
        dashboardControlId?: string;
        controlName?: string;
        tabs?: Tab[];
        value?: Tab;
    }) {
        const test: any = UpdateTabsPayloadSchema.safeParse(payload);
        if (!test.success) {
            console.warn('UpdateTabsPayloadSchema Invalid Payload');
            console.log(test.error.issues);
            return;
        }
        this._statusService.updateTabs(
            payload.dashboardControlId,
            payload.controlName,
            payload.tabs,
            payload.value
        );
    }
    updateControlLabel(payload: UpdateLabelPayload) {
        const test: any = UpdateLabelPayloadSchema.safeParse(payload);

        if (!test.success) {
            console.warn('UpdateLabelPayloadSchema Invalid Payload');
            console.log(test.error.issues);
            return;
        }

        this._statusService.updateControlLabel(
            payload.dashboardControlId,
            payload.controlName,
            payload.label,
            payload.checkedLabel,
            payload.uncheckedLabel,
        );
    }

    register(): void {
        this._events.registerMap({
            setDashboardControlsValue: {
                callback: (payload: SetDashboardControlsValuePayload) =>
                    this.setDashboardControlsValue(payload),
            },
            patchDashboardControlsValue: {
                callback: (payload: PatchDashboardControlsValuePayload) =>
                    this.patchDashboardControlsValue(payload),
            },
            setDashboardControlValue: {
                callback: (payload: SetControlValuePayload) =>
                    this.setControlValue(payload),
            },
            setControlProp: {
                callback: (payload: SetControlPropPayload) =>
                    this.setControlProp(payload),
            },
            addControl: {
                callback: (payload: AddControlPayload) =>
                    this.addControl(payload),
            },
            removeControl: {
                callback: (payload: RemoveControlPayload) =>
                    this.removeControl(payload),
            },
            updateFormCache: {
                callback: (payload: UpdateFormCachePayload) =>
                    this.updateFormCache(payload),
            },
            enableControl: {
                callback: (payload: EnableControlPayload) =>
                    this.enableControl(payload),
            },
            disableControl: {
                callback: (payload: DisableControlPayload) =>
                    this.disableControl(payload),
            },
            'type:tabs:update-tabs': {
                callback: (payload: UpdateTabsPayload) => {
                    this.typeTabsUpdateTabs(payload);
                },
            },
            updateControlLabel: {
                callback: (payload: UpdateLabelPayload) => {
                    this.updateControlLabel(payload);
                },
            },
        });
    }
}
